import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useField, useFormikContext } from 'formik'
import { MuiPickersUtilsProvider, KeyboardTimePicker } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { DATE_AND_TIME_FIELD_MODES } from '../formDefinitions'
import { fetchDateAndTimeFieldSetup } from '../smartFormHelpers'
import { makeStyles, createTheme, ThemeProvider } from '@material-ui/core/styles'
import errorIcon from 'images/overdue.svg'
import { ReactComponent as TimePickerIcon } from 'images/timepicker-icon.svg'

const useStyles = (vstTheme) =>
  makeStyles(() => ({
    root: {},
    labelRoot: {
      position: 'relative',
      transform: 'none !important',
      color: '#1C1C1C',
      fontFamily: 'Roboto',
      fontSize: '16px',
      lineHeight: '20px',
      fontWeight: 500,
      margin: '0 0 7px',
    },
    focusedLabel: {
      color: '#1C1C1C !important',
    },
    labelError: {
      color: '#1C1C1C !important',
    },
    notchedOutline: {
      border: 'none',
    },
    inputRoot: {
      border: '1px solid rgba(0, 0, 0, 0.43)',
      '& > div': {
        color: '#1C1C1C',
        fontFamily: 'Roboto',
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: 400,
      },
    },
    editHighlight: {
      boxShadow: 'inset black 0px 0px 0px 2px',
    },
    inputError: {
      borderColor: `${vstTheme?.vstui?.color?.error} !important`,
      '& svg': {
        fill: `${vstTheme?.vstui?.color?.error} !important`,
      },
    },
    inputDisabled: {
      '& svg': {
        fill: 'rgba(0, 0, 0, 0.26) !important',
      },
    },
    selectIcon: {
      width: '13px',
      height: '8px',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      fill: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
    },
    helperTextRoot: {
      position: 'relative',
      fontFamily: 'Roboto',
      fontSize: '16px',
      lineHeight: '24px',
      marginLeft: '24px',
    },
    helperTextError: {
      color: '#D21C35 !important',
      '&::before': {
        content: "''",
        backgroundImage: `url(${errorIcon})`,
        transform: 'translate(-20px, 3px)',
        height: '16px',
        width: '16px',
        position: 'absolute',
        top: '0',
      },
    },
    adornmentPosition: {
      '& > button': {
        transform: 'translateX(15px) !important',
      },
    },
    pickerIcon: {
      height: '17px',
      width: '17px',
      fill: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
    },
  }))()

const usePickerTheme = (vstTheme) =>
  createTheme({
    overrides: {
      MuiPickersToolbar: {
        toolbar: {
          backgroundColor: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
        },
      },
      MuiPickersCalendarHeader: {
        switchHeader: {
          backgroundColor: 'white',
        },
      },
      MuiPickersClock: {
        pin: {
          backgroundColor: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
        },
      },
      MuiPickersClockPointer: {
        pointer: {
          backgroundColor: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
        },
        noPoint: {
          backgroundColor: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
        },
        thumb: {
          borderColor: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
        },
      },
      MuiButton: {
        textPrimary: {
          color: `${vstTheme?.vstui?.color?.primary?.getShade(6)}`,
        },
      },
      MuiDialogActions: {
        root: {
          '& button': {
            '&:hover': {
              backgroundColor: '#EEEEEE',
            },
          },
        },
      },
    },
  })

const TimePicker = ({ theme, required, disabled, mode, showEmptyLabel, editedField, ...props }) => {
  const { t } = useTranslation()
  const { values } = useFormikContext()
  const [field, meta, helpers] = useField(props)
  const classes = useStyles(theme)
  const pickerTheme = usePickerTheme(theme)

  const onChange = useCallback(
    (time) => {
      helpers.setValue(time)
      if (meta.error) {
        helpers.setError(null)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [helpers]
  )

  const fetchFieldLabel = useCallback(() => {
    const { formFields, multipleValuesOption } = fetchDateAndTimeFieldSetup(mode)
    if (mode && values[formFields.select.name] === multipleValuesOption.value) {
      return showEmptyLabel ? '' : t(multipleValuesOption.i18n)
    }
    return t('settingsForm.timePlaceholder')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, mode, values])

  return (
    <div className="time-picker">
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <ThemeProvider theme={pickerTheme}>
          <KeyboardTimePicker
            id={props.name}
            defaultValue={props.defaultValue}
            label={props.label}
            value={field.value}
            disabled={disabled}
            onBlur={field.onBlur}
            mask={t('settingsForm.timePlaceholder') || '__:__ _M'}
            format={t('settingsForm.timeFormat') || 'hh:mm A'}
            refuse={/[^\dmap]+/gi}
            emptyLabel={fetchFieldLabel()}
            onChange={onChange}
            error={meta.error ? true : false}
            helperText={meta.error ? meta.error : null}
            inputProps={{ name: props.name }}
            classes={{ root: classes.root }}
            required={required}
            keyboardIcon={<TimePickerIcon className={classes.pickerIcon} />}
            KeyboardButtonProps={{
              'aria-label': 'change time',
            }}
            inputVariant="outlined"
            InputProps={{
              classes: {
                notchedOutline: classes.notchedOutline,
                root: classNames(classes.inputRoot, { [classes.editHighlight]: editedField }),
                error: classes.inputError,
                disabled: classes.inputDisabled,
              },
            }}
            InputLabelProps={{
              classes: {
                root: classes.labelRoot,
                focused: classes.focusedLabel,
                error: classes.labelError,
              },
            }}
            FormHelperTextProps={{
              classes: {
                root: classes.helperTextRoot,
                error: classes.helperTextError,
              },
            }}
            InputAdornmentProps={{
              classes: {
                root: classes.adornmentPosition,
              },
            }}
            fullWidth
            margin="dense"
          />
        </ThemeProvider>
      </MuiPickersUtilsProvider>
    </div>
  )
}

export default TimePicker

TimePicker.propTypes = {
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  editedField: PropTypes.bool,
  label: PropTypes.string,
  mode: PropTypes.oneOf([
    DATE_AND_TIME_FIELD_MODES.availableDate,
    DATE_AND_TIME_FIELD_MODES.dueDate,
    undefined,
  ]),
  name: PropTypes.string,
  required: PropTypes.bool,
  showEmptyLabel: PropTypes.bool,
  theme: PropTypes.object,
}

TimePicker.defaultProps = {
  disabled: false,
  editedField: false,
  mode: undefined,
  required: false,
  showEmptyLabel: false,
  theme: {},
}
